import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { GraduationCap, Star, TrendingUp, ClipboardList, Grid3x3, Swords, Compass, Upload, Plus, X, Users, Circle, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { LEVELS, CHARACTERS } from '../lib/levels';
import { useT } from '../lib/i18n';
import AppHeader from './AppHeader';

const getLevel = (points) => LEVELS.find((l) => points >= l.min && points <= l.max) || LEVELS[0];

const SUBJECTS = ['math', 'science', 'geography', 'history', 'language'];

const StatusDot = ({ status }) => {
  const color = status === 'green' ? 'bg-teal' : status === 'red' ? 'bg-red-500' : 'bg-gold';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} data-testid={`status-dot-${status}`} />;
};

// ---------- Overview tab ----------
const OverviewTab = ({ user, students, t }) => {
  const totalPoints = students.reduce((s, x) => s + (x.points || 0), 0);
  const avg = students.length ? Math.round(totalPoints / students.length) : 0;
  return (
    <>
      <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream">
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap className="w-5 h-5 text-teal" />
          <span className="text-teal font-heading font-semibold text-sm uppercase tracking-wider">{t('teacher.kicker')}</span>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">{t('teacher.welcome')} {user?.name?.split(' ').slice(-1)}</h1>
        <p className="text-navy/70 mt-1">{t('teacher.subtitle')}</p>
      </section>
      <section className="grid grid-cols-3 gap-3" data-testid="teacher-stats">
        <div className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-cream text-center">
          <div className="text-3xl font-heading font-bold text-navy" data-testid="stat-students">{students.length}</div>
          <div className="text-xs text-navy/60 mt-1">{t('teacher.students')}</div>
        </div>
        <div className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-cream text-center">
          <div className="text-3xl font-heading font-bold text-teal" data-testid="stat-avg">{avg}</div>
          <div className="text-xs text-navy/60 mt-1">{t('teacher.avg_points')}</div>
        </div>
        <div className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-cream text-center">
          <div className="text-3xl font-heading font-bold text-gold" data-testid="stat-total">{totalPoints}</div>
          <div className="text-xs text-navy/60 mt-1">{t('teacher.total_pts')}</div>
        </div>
      </section>
      <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="students-list-card">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-teal" />
          <h2 className="font-heading text-xl font-bold text-navy">{t('teacher.progress')}</h2>
        </div>
        {students.length === 0 ? <p className="text-navy/60 text-center py-6">{t('teacher.no_students')}</p> : (
          <div className="space-y-3">
            {students.slice().sort((a, b) => (b.points || 0) - (a.points || 0)).map((s, i) => {
              const level = s.level || getLevel(s.points || 0);
              const character = CHARACTERS[s.character] || CHARACTERS.patapouf;
              return (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-2xl bg-cream" data-testid={`student-row-${s.id}`}>
                  <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">{i + 1}</div>
                  <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-teal shrink-0">
                    <img src={character.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-navy truncate">{s.name}</div>
                    <div className="text-xs text-navy/60">{s.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/20 text-navy text-xs font-bold">
                      <Star className="w-3 h-3 fill-current text-gold" /> {t(`level.${level.name.toLowerCase()}`)}
                    </div>
                    <div className="text-xs text-navy/60 mt-1">{s.points || 0} {t('level.pts')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

// ---------- Class Grid ----------
const ClassGridTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/teacher/class-grid'); setRows(data.students); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);
  if (loading) return <div className="text-navy/60 text-center py-8">Loading grid…</div>;
  return (
    <section data-testid="class-grid">
      <div className="flex items-center gap-4 mb-4 text-xs text-navy/70">
        <span className="flex items-center gap-1"><StatusDot status="green" /> Active today</span>
        <span className="flex items-center gap-1"><StatusDot status="yellow" /> Not started today</span>
        <span className="flex items-center gap-1"><StatusDot status="red" /> Struggling</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((r) => {
          const character = CHARACTERS[r.character] || CHARACTERS.patapouf;
          return (
            <div key={r.id} className="bg-white rounded-3xl border-2 border-cream shadow-squishy p-4" data-testid={`grid-cell-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-teal shrink-0">
                  <img src={character.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusDot status={r.status} />
                    <span className="font-heading font-bold text-navy truncate">{r.name}</span>
                  </div>
                  <div className="text-xs text-navy/60 truncate">{r.email}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-cream rounded-xl p-2">
                  <div className="font-heading font-bold text-navy text-lg">{r.points}</div>
                  <div className="text-[10px] text-navy/60 uppercase tracking-wider">pts</div>
                </div>
                <div className="bg-cream rounded-xl p-2">
                  <div className="font-heading font-bold text-teal text-lg flex items-center justify-center gap-1"><Flame className="w-4 h-4" />{r.streak_days}</div>
                  <div className="text-[10px] text-navy/60 uppercase tracking-wider">streak</div>
                </div>
                <div className="bg-cream rounded-xl p-2">
                  <div className="font-heading font-bold text-gold text-lg">{r.questions_today}</div>
                  <div className="text-[10px] text-navy/60 uppercase tracking-wider">today</div>
                </div>
              </div>
              {r.topics_today && r.topics_today.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.topics_today.map((s) => (
                    <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-teal/10 text-teal font-bold uppercase tracking-wider">{s}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ---------- Assignments ----------
const AssignmentsTab = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', subject: 'math', count: 5, student_emails: '', due_at: '' });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = useCallback(async () => {
    try { const { data } = await api.get('/teacher/assignments'); setItems(data.assignments); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const emails = form.student_emails.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const payload = { title: form.title, description: form.description || null, subject: form.subject, count: Number(form.count), student_emails: emails };
      if (form.due_at) payload.due_at = new Date(form.due_at).toISOString();
      const { data } = await api.post('/teacher/assignments', payload);
      toast.success(`Assigned to ${data.student_count} student(s)`);
      setShowForm(false);
      setForm({ title: '', description: '', subject: 'math', count: 5, student_emails: '', due_at: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="assignments-tab">
      <button onClick={() => setShowForm((v) => !v)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold text-navy font-heading font-bold shadow-gold hover:scale-105 active:scale-95 transition-transform mb-4"
        data-testid="new-assignment-button">
        <Plus className="w-4 h-4" /> New Assignment
      </button>
      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-3xl border-2 border-teal p-5 space-y-3 mb-4 shadow-squishy" data-testid="assignment-form">
          <input required placeholder="Title" value={form.title} onChange={(e) => upd('title', e.target.value)} className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="assign-title" />
          <textarea placeholder="Description (optional)" rows={2} value={form.description} onChange={(e) => upd('description', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.subject} onChange={(e) => upd('subject', e.target.value)} className="h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none bg-white" data-testid="assign-subject">
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="number" min={1} max={100} value={form.count} onChange={(e) => upd('count', e.target.value)} placeholder="# Questions" className="h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="assign-count" />
          </div>
          <input type="datetime-local" value={form.due_at} onChange={(e) => upd('due_at', e.target.value)} className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          <textarea required placeholder="Student emails (comma or newline separated)" rows={2} value={form.student_emails} onChange={(e) => upd('student_emails', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="assign-emails" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-full bg-white border-2 border-navy text-navy font-heading font-bold">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 h-11 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60" data-testid="assign-submit">{busy ? 'Creating…' : 'Assign'}</button>
          </div>
        </form>
      )}
      {items.length === 0 ? <p className="text-navy/60 text-sm">No assignments yet.</p> : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border-2 border-cream p-4" data-testid={`assignment-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-navy truncate">{a.title}</div>
                  <div className="text-xs text-navy/60 mt-0.5">{a.subject} · {a.count} questions · {a.student_ids.length} student(s)</div>
                  {a.description && <div className="text-sm text-navy/70 mt-1">{a.description}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ---------- Challenges ----------
const ChallengesTab = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ opponent_teacher_email: '', my_class_name: '', opponent_class_name: '', start_at: '', end_at: '' });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = useCallback(async () => {
    try { const { data } = await api.get('/teacher/challenges'); setItems(data.challenges); } catch {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/teacher/challenges', {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      });
      toast.success('Challenge sent 💪');
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const accept = async (c) => {
    try { await api.post(`/teacher/challenges/${c.id}/accept`); toast.success('Accepted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  return (
    <section data-testid="challenges-tab">
      <button onClick={() => setShowForm((v) => !v)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold text-navy font-heading font-bold shadow-gold hover:scale-105 active:scale-95 transition-transform mb-4"
        data-testid="new-challenge-button">
        <Swords className="w-4 h-4" /> Challenge Another Class
      </button>
      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-3xl border-2 border-teal p-5 space-y-3 mb-4 shadow-squishy" data-testid="challenge-form">
          <input required type="email" placeholder="Opponent teacher email" value={form.opponent_teacher_email} onChange={(e) => upd('opponent_teacher_email', e.target.value)} className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="challenge-opponent" />
          <div className="grid grid-cols-2 gap-2">
            <input required placeholder="My class name" value={form.my_class_name} onChange={(e) => upd('my_class_name', e.target.value)} className="h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
            <input required placeholder="Their class name" value={form.opponent_class_name} onChange={(e) => upd('opponent_class_name', e.target.value)} className="h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input required type="datetime-local" value={form.start_at} onChange={(e) => upd('start_at', e.target.value)} className="h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
            <input required type="datetime-local" value={form.end_at} onChange={(e) => upd('end_at', e.target.value)} className="h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-full bg-white border-2 border-navy text-navy font-heading font-bold">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 h-11 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60" data-testid="challenge-submit">{busy ? 'Sending…' : 'Send Challenge'}</button>
          </div>
        </form>
      )}
      {items.length === 0 ? <p className="text-navy/60 text-sm">No challenges yet.</p> : (
        <div className="space-y-3">
          {items.map((c) => {
            const hostWinning = c.host_points >= c.challenger_points;
            return (
              <div key={c.id} className="bg-white rounded-3xl border-2 border-cream p-5 shadow-squishy" data-testid={`challenge-row-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-teal">{c.status}</span>
                  <span className="text-xs text-navy/60">{new Date(c.start_at).toLocaleDateString()} → {new Date(c.end_at).toLocaleDateString()}</span>
                </div>
                <div className="grid grid-cols-3 items-center gap-2">
                  <div className={`text-center p-3 rounded-2xl ${hostWinning ? 'bg-gold/20 border-2 border-gold' : 'bg-cream'}`}>
                    <div className="font-heading font-bold text-navy text-sm truncate">{c.host_class_name}</div>
                    <div className="text-2xl font-heading font-bold text-navy mt-1" data-testid={`host-points-${c.id}`}>{c.host_points}</div>
                    <div className="text-[10px] text-navy/60 uppercase">host</div>
                  </div>
                  <div className="text-center text-3xl font-heading font-bold text-navy/40">VS</div>
                  <div className={`text-center p-3 rounded-2xl ${!hostWinning ? 'bg-gold/20 border-2 border-gold' : 'bg-cream'}`}>
                    <div className="font-heading font-bold text-navy text-sm truncate">{c.challenger_class_name}</div>
                    <div className="text-2xl font-heading font-bold text-navy mt-1" data-testid={`chal-points-${c.id}`}>{c.challenger_points}</div>
                    <div className="text-[10px] text-navy/60 uppercase">rival</div>
                  </div>
                </div>
                {c.status === 'pending' && !c.is_host && (
                  <button onClick={() => accept(c)} className="mt-3 w-full h-11 rounded-full bg-teal text-white font-heading font-bold" data-testid={`accept-challenge-${c.id}`}>Accept Challenge</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ---------- Quests (teacher can create custom) ----------
const QuestsTab = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', subject: 'math', count: 10, badge: '🏅' });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = useCallback(async () => {
    try { const { data } = await api.get('/quests'); setItems(data.quests); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/teacher/quests', { ...form, count: Number(form.count) });
      toast.success('Quest created 🎯');
      setShowForm(false);
      setForm({ name: '', description: '', subject: 'math', count: 10, badge: '🏅' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  return (
    <section data-testid="quests-tab">
      <button onClick={() => setShowForm((v) => !v)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold text-navy font-heading font-bold shadow-gold hover:scale-105 active:scale-95 transition-transform mb-4"
        data-testid="new-quest-button">
        <Plus className="w-4 h-4" /> Create Custom Quest
      </button>
      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-3xl border-2 border-teal p-5 space-y-3 mb-4 shadow-squishy" data-testid="quest-form">
          <input required placeholder="Quest name" value={form.name} onChange={(e) => upd('name', e.target.value)} className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          <textarea placeholder="Story description" rows={2} value={form.description} onChange={(e) => upd('description', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" />
          <div className="grid grid-cols-3 gap-2">
            <select value={form.subject} onChange={(e) => upd('subject', e.target.value)} className="h-12 px-3 rounded-xl border-2 border-slate-200 bg-white">
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="number" min={1} max={100} placeholder="# Questions" value={form.count} onChange={(e) => upd('count', e.target.value)} className="h-12 px-3 rounded-xl border-2 border-slate-200" />
            <input placeholder="Badge emoji" value={form.badge} onChange={(e) => upd('badge', e.target.value)} maxLength={4} className="h-12 px-3 rounded-xl border-2 border-slate-200 text-center text-2xl" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-full bg-white border-2 border-navy text-navy font-heading font-bold">Cancel</button>
            <button type="submit" className="flex-1 h-11 rounded-full bg-gold text-navy font-heading font-bold shadow-gold" data-testid="quest-submit">Create</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((q) => (
          <div key={q.id} className={`rounded-3xl border-2 p-5 shadow-squishy ${q.completed ? 'bg-teal/10 border-teal' : 'bg-white border-cream'}`} data-testid={`quest-row-${q.id}`}>
            <div className="flex items-start gap-3">
              <div className="text-4xl">{q.badge || '🏅'}</div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-navy">{q.name}</div>
                <div className="text-xs text-navy/60 mt-0.5">{q.description}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs font-semibold text-navy/70 mb-1">
                <span>{q.subject} · {q.progress}/{q.count}</span>
                {q.completed && <span className="text-teal font-bold">Completed ✓</span>}
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-[width] duration-500" style={{ width: `${(q.progress / q.count) * 100}%` }} />
              </div>
            </div>
            {!q.is_default && q.teacher_name && <div className="mt-2 text-[11px] text-navy/50">Created by {q.teacher_name}</div>}
          </div>
        ))}
      </div>
    </section>
  );
};

// ---------- Import ----------
const ImportTab = () => {
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };
  const submit = async (e) => {
    e.preventDefault();
    const rows = csvText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const students = [];
    for (const line of rows) {
      const parts = line.split(/,|;|\t/).map((s) => s.trim());
      const email = parts.find((p) => p.includes('@'));
      if (!email) continue;
      const name = parts.filter((p) => p !== email).find(Boolean);
      students.push({ email, name });
    }
    if (students.length === 0) { toast.error('No valid email rows found'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/teacher/import-students', { students });
      setResult(data);
      toast.success(`Imported ${data.created.length} student(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="import-tab">
      <div className="bg-gradient-to-r from-gold/20 to-teal/20 border-2 border-gold rounded-3xl p-5 mb-4 flex items-start gap-3" data-testid="mangahigh-banner">
        <div className="text-4xl">👋</div>
        <div>
          <h3 className="font-heading font-bold text-navy">Migrating from Mangahigh?</h3>
          <p className="text-navy/70 text-sm">Upload your class list (CSV) with columns for name + email — we'll create accounts and link them to your class instantly.</p>
        </div>
      </div>
      <form onSubmit={submit} className="bg-white rounded-3xl border-2 border-cream p-5 shadow-squishy space-y-3" data-testid="import-form">
        <label className="block">
          <span className="block text-sm font-heading font-bold text-navy mb-2">Upload CSV file</span>
          <input type="file" accept=".csv,text/csv,text/plain" onChange={handleFile} className="w-full text-sm" data-testid="csv-upload" />
        </label>
        <div>
          <span className="block text-sm font-heading font-bold text-navy mb-2">…or paste rows: <span className="font-normal text-navy/60">name, email</span></span>
          <textarea rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)}
            placeholder={"Anna Smith, anna@school.edu\nJake Kim, jake@school.edu"}
            className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-mono text-sm"
            data-testid="csv-textarea" />
        </div>
        <button type="submit" disabled={busy || !csvText.trim()}
          className="w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="import-submit">
          <Upload className="w-4 h-4" /> {busy ? 'Importing…' : 'Import Class'}
        </button>
      </form>
      {result && (
        <div className="mt-4 bg-white rounded-3xl border-2 border-cream p-5" data-testid="import-result">
          <div className="font-heading font-bold text-navy mb-2">Created {result.created.length} · Skipped {result.skipped.length}</div>
          {result.credentials.length > 0 && (
            <div className="bg-cream rounded-2xl p-3 mt-2 max-h-48 overflow-y-auto">
              <div className="text-xs text-navy/70 mb-2">Share these temporary passwords with each student:</div>
              {result.credentials.map((c) => (
                <div key={c.email} className="flex justify-between text-sm font-mono py-0.5">
                  <span className="truncate">{c.email}</span>
                  <span className="text-teal font-bold ml-2">{c.temp_password}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// ---------- Main ----------
export default function TeacherDashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const [tab, setTab] = useState('overview');
  const [students, setStudents] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/teacher/students'); setStudents(data.students); }
      catch { toast.error('Could not load'); }
    })();
  }, []);

  const tabs = [
    { k: 'overview',    label: 'Overview',    icon: TrendingUp },
    { k: 'grid',        label: 'Class Grid',  icon: Grid3x3 },
    { k: 'assignments', label: 'Assignments', icon: ClipboardList },
    { k: 'challenges',  label: 'Challenges',  icon: Swords },
    { k: 'quests',      label: 'Quests',      icon: Compass },
    { k: 'import',      label: 'Import',      icon: Upload },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader subtitle={t('header.dashboard_teacher')} />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="teacher-dashboard">
        {/* Tabs — horizontally scrollable on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-testid="teacher-tabs">
          {tabs.map((tv) => {
            const Icon = tv.icon;
            const active = tab === tv.k;
            return (
              <button key={tv.k} onClick={() => setTab(tv.k)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-heading font-bold text-sm border-2 transition-colors duration-200 ${active ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-cream hover:border-teal'}`}
                data-testid={`teacher-tab-${tv.k}`}>
                <Icon className="w-4 h-4" /> {tv.label}
              </button>
            );
          })}
        </div>
        {tab === 'overview'    && <OverviewTab user={user} students={students} t={t} />}
        {tab === 'grid'        && <ClassGridTab />}
        {tab === 'assignments' && <AssignmentsTab />}
        {tab === 'challenges'  && <ChallengesTab />}
        {tab === 'quests'      && <QuestsTab />}
        {tab === 'import'      && <ImportTab />}
      </main>
    </div>
  );
}
